import axios from 'axios';
import { mockProducts, mockVendors, mockOrders } from '../data/mockData';

// Use Firebase Functions endpoint or fallback to localhost
const API_URL = process.env.REACT_APP_API_URL || 
  (process.env.NODE_ENV === 'production' ? '/api' : 'http://127.0.0.1:5001/your-project-id/us-central1/api');

// Enable mock data only for local development without backend
const USE_MOCK_DATA = process.env.REACT_APP_USE_MOCK_DATA === 'true' || 
  (process.env.NODE_ENV === 'development' && !process.env.REACT_APP_API_URL);

// Mock API response helper
const createMockResponse = (data, success = true) => {
  return Promise.resolve({
    data: {
      success,
      data: {
        data,
        total: Array.isArray(data) ? data.length : 1
      },
      message: success ? 'Success' : 'Error'
    }
  });
};

// Filter mock data based on parameters
const filterMockData = (data, params = {}) => {
  let filtered = [...data];
  
  if (params.search) {
    const search = params.search.toLowerCase();
    filtered = filtered.filter(item => 
      (item.product_name && item.product_name.toLowerCase().includes(search)) ||
      (item.vendor_name && item.vendor_name.toLowerCase().includes(search)) ||
      (item.description && item.description.toLowerCase().includes(search))
    );
  }
  
  if (params.category) {
    filtered = filtered.filter(item => item.category === params.category);
  }
  
  if (params.vendor_id) {
    filtered = filtered.filter(item => item.vendor_id === parseInt(params.vendor_id));
  }
  
  if (params.user_id) {
    filtered = filtered.filter(item => item.user_id === parseInt(params.user_id));
  }
  
  return filtered;
};

// Create axios instance
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('auth_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('auth_token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth APIs
export const authAPI = {
  register: (userData) => api.post('/register', userData),
  login: (credentials) => api.post('/login', credentials),
  logout: () => api.post('/logout'),
  me: () => api.get('/me'),
};

// Vendor APIs
export const vendorAPI = {
  getAll: (params = {}) => {
    if (USE_MOCK_DATA) {
      const filtered = filterMockData(mockVendors, params);
      return createMockResponse(filtered);
    }
    return api.get('/vendors', { params });
  },
  getById: (id) => {
    if (USE_MOCK_DATA) {
      const vendor = mockVendors.find(v => v.id === parseInt(id));
      return createMockResponse(vendor);
    }
    return api.get(`/vendors/${id}`);
  },
  create: (vendorData) => api.post('/vendors', vendorData),
  update: (id, vendorData) => api.put(`/vendors/${id}`, vendorData),
  delete: (id) => api.delete(`/vendors/${id}`),
};

// Product APIs
export const productAPI = {
  getAll: (params = {}) => {
    if (USE_MOCK_DATA) {
      const filtered = filterMockData(mockProducts, params);
      return createMockResponse(filtered);
    }
    return api.get('/products', { params });
  },
  getById: (id) => {
    if (USE_MOCK_DATA) {
      const product = mockProducts.find(p => p.id === parseInt(id));
      return createMockResponse(product);
    }
    return api.get(`/products/${id}`);
  },
  create: (productData) => api.post('/products', productData),
  update: (id, productData) => api.put(`/products/${id}`, productData),
  delete: (id) => api.delete(`/products/${id}`),
};

// Order APIs
export const orderAPI = {
  getAll: (params = {}) => {
    if (USE_MOCK_DATA) {
      const filtered = filterMockData(mockOrders, params);
      return createMockResponse(filtered);
    }
    return api.get('/orders', { params });
  },
  getById: (id) => {
    if (USE_MOCK_DATA) {
      const order = mockOrders.find(o => o.id === parseInt(id));
      return createMockResponse(order);
    }
    return api.get(`/orders/${id}`);
  },
  create: (orderData) => api.post('/orders', orderData),
  update: (id, orderData) => api.put(`/orders/${id}`, orderData),
  delete: (id) => api.delete(`/orders/${id}`),
};

export default api;
